import { useEffect, useState } from "react";
import { baseUrl } from "../../../Baseurl/Baseurl";
import { Col, Card } from "reactstrap";
import {
  FaUsers,
  FaChartPie,
  FaBuilding,
  FaHandHoldingMedical,
} from "react-icons/fa";
import { CgSandClock } from "react-icons/cg";
import api from "../../../../services/api";
import dayjs from "dayjs";
import { toast } from "react-toastify";

interface DepartmentToken {
  departmentId: number;
  tokenCount: number;
  Department?: {
    id: number;
    departmentname: string;
  };
}

const DashboardTokencounts = () => {
  const [totalBookedTokens, setTotalBookedTokens] = useState<number>(0);
  const [departmentTokens, setDepartmentTokens] = useState<DepartmentToken[]>(
    []
  );
  const [totalTokens, setTotalTokens] = useState<number>(0);
  const [balanceTokens, setBalanceTokens] = useState<number>(0);
  const [upcomingDate, setUpcomingDate] = useState("");

  const tokenCardListData = [
    {
      id: 1,
      color: "primary",
      title: "Total Available Tokens",
      count: totalTokens,
      icon: "users", // Add an icon property
    },
    {
      id: 2,
      color: "primary",
      title: "Total Tokens for Saturday",
      count: totalBookedTokens,
      icon: "chart", // Add an icon property
    },
    {
      id: 3,
      color: "primary",
      title: "Remaining Tokens",
      count: balanceTokens,
      icon: "CgSandClock", // You can assign an appropriate icon here
    },
  ];

  useEffect(() => {
    fetchAssignedDates();
  }, []);

  // Fetch assigned dates and determine the next upcoming date
  const fetchAssignedDates = async () => {
    try {
      const response = await api.get(`${baseUrl}assignedDates/getAllDates`);
      console.log("Fetched Assigned Dates:", response.data);

      // Extract and parse dates from response
      const extractedDates: dayjs.Dayjs[] = response.data.map(
        (item: { dates: string }) => dayjs(item.dates).startOf("day") // Normalize to remove time component
      );

      // Sort dates in ascending order
      const sortedDates = extractedDates.sort(
        (a: dayjs.Dayjs, b: dayjs.Dayjs) => a.valueOf() - b.valueOf()
      );

      // Get today's date
      const today = dayjs().startOf("day");

      // Find the next upcoming date
      const nextDate = sortedDates.find((date: dayjs.Dayjs) =>
        date.isAfter(today)
      );

      if (nextDate) {
        setUpcomingDate(nextDate.format("DD/MM/YYYY")); // Format as needed
      } else {
        setUpcomingDate("No upcoming dates available");
      }
    } catch (error) {
      console.error("Failed to fetch assigned dates:", error);
      toast.error("Failed to fetch assigned dates.");
    }
  };

  useEffect(() => {
    if (!upcomingDate) return; // Ensure upcomingDate is available before making request

    const fetchTokenData = async () => {
      try {
        const response = await api.get(
          `${baseUrl}tokenregistration/weeklyTokenCount`,
          {
            params: { date: upcomingDate }, // Passing upcomingDate to backend
          }
        );
    
        console.log("Received Data:", response.data);
    
        // Transform API response to expected structure
        const transformedData = response.data.tokenCountByDepartment.map(
          (dept: any) => ({
            departmentId: dept.departmentId,
            tokenCount: dept.tokenCount,
            Department: {
              id: dept["Department.id"], // Convert flattened keys to nested object
              departmentname: dept["Department.departmentname"],
            },
          })
        );
    
        setTotalBookedTokens(response.data.totalBookedTokens);
        setDepartmentTokens(transformedData); // Use transformed data
        setTotalTokens(response.data.totalTokens);
        setBalanceTokens(response.data.balanceTokens);
      } catch (error) {
        console.error("Error fetching token data:", error);
        toast.error("Failed to fetch token data.");
      }
    };
    fetchTokenData();
  }, [upcomingDate]); // Trigger when upcomingDate is set

  return (
    <Col sm={12}>
      <Card>
        <div className="container mt-4">
          {/* Token Cards */}
          <div className="row justify-content-center">
            {tokenCardListData.map((item) => (
              <div
                className="col-md-4 d-flex justify-content-center"
                key={item.id}
              >
                <div
                  className={`card text-white shadow-lg rounded-lg bg-${item.color} mb-3`}
                  style={{ width: "20rem", transition: "0.3s ease-in-out" }}
                >
                  <div className="card-body text-center">
                    <div className="icon-container mb-2">
                      {item.icon === "users" && <FaUsers size={40} />}
                      {item.icon === "chart" && <FaChartPie size={40} />}
                      {item.icon === "CgSandClock" && <CgSandClock size={40} />}
                    </div>
                    <h5 className="card-title">{item.title}</h5>
                    <p
                      className="card-text"
                      style={{ fontSize: "1.8rem", fontWeight: "bold" }}
                    >
                      {item.count}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <hr />

          {/* Department-wise Tokens */}
          <div className="text-center my-3">
            <h3>Department-wise Tokens</h3>
          </div>

          <div className="row justify-content-center">
            {departmentTokens.map((dept, index) => (
              <div
                className="col-md-4 d-flex justify-content-center"
                key={index}
              >
                <div
                  className="card shadow-lg bg-primary text-white mb-3"
                  style={{ width: "20rem", transition: "0.3s ease-in-out" }}
                >
                  <div className="card-body text-center">
                    <div className="icon-container mb-2">
                      <FaHandHoldingMedical size={40} />
                    </div>
                    <h5 className="card-title">
                      {dept.Department?.departmentname ?? "Unknown"}
                    </h5>
                    <p
                      className="card-text"
                      style={{ fontSize: "1.2rem", fontWeight: "bold" }}
                    >
                      {dept.tokenCount || 0} Tokens
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </Col>
  );
};

export default DashboardTokencounts;
